import { Alert, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useState } from 'react';
import { rawApi } from '../api/client';
import type { ApiError } from '../api/errors';
import { formatMoney } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from './WritingIntoAFiledMonth';

/**
 * The one dialog for taking a payment back — a supplier's bill or a partner's commission.
 *
 * Written once because the two are the same gesture with a different noun in front of it, and
 * because the half that is easy to get wrong is identical on both: **what happens to the register.**
 * The API answers that in facts rather than in a verdict (`CashOutcome`), and this file is the one
 * place those facts are turned into a sentence. Two copies of that wording would drift, and the
 * drift would be in the sentence that tells somebody whether the cash in front of them has been
 * accounted for.
 *
 * ## Why the reason is required and not optional
 *
 * The person who reads it is not the person typing it. A payment taken back is a figure that moved
 * twice, and six weeks later the only question anybody asks about it is why — from the owner
 * checking a vendor's statement, from a manager looking at an over on a drawer, or from an
 * accountant. "Corrected" answers none of them, but a box that can be left empty is a box that is
 * left empty, so the floor is the same three characters every other reason in this portal uses.
 */

/** What the API says happened to the drawer. Facts, so a new one can be added without breaking us. */
export interface CashOutcome {
  drawerSessionId: string | null;
  putBackIntoTheRegister: boolean;
  registerAlreadyCountedOff: boolean;
  registerHandedItOverAtTheDoor: boolean;
}

/**
 * What to tell the owner about the cash, in the words that match what actually happened.
 *
 * The third and fourth cases are the ones worth saying out loud. A drawer that has been counted off
 * is not going to be touched, and somebody holding the notes needs to know that rather than assume
 * the software has dealt with it. A movement the register made at the door is money that genuinely
 * left, and the useful thing to say is that it can now be recorded against what it really paid for.
 */
export function whatHappenedToTheRegister(cash: CashOutcome, amount: string): string {
  if (cash.putBackIntoTheRegister) {
    return `${amount} has gone back into the register, so that drawer still counts off level.`;
  }
  if (cash.registerAlreadyCountedOff) {
    return `That drawer has already been counted off, so nothing moved — the difference is in that night's over and short. The cash is yours to put back where it came from.`;
  }
  if (cash.registerHandedItOverAtTheDoor) {
    return 'The register handed that cash over itself, so it stays where it is — it can now be recorded against whatever it actually paid for.';
  }
  return 'No register was involved, so no drawer moved.';
}

export interface TakingAPaymentBackProps {
  /** The API door. `POST` with `{ reason }`, answering `{ cash }`. */
  path: string;
  /** What is being undone, as a person would say it: "L 10,000.00 to Madeyso Gas". */
  subject: string;
  amountMinor: number;
  currency: string;
  /** Extra sentence about what it does to the thing being paid — a bill, an envelope. */
  consequence: string;
  onClose: () => void;
  onDone: () => void;
}

export function TakingAPaymentBack(props: TakingAPaymentBackProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filedMonth, setFiledMonth] = useState<ApiError | null>(null);
  const tooShort = reason.trim().length < 3;
  const amount = formatMoney(props.amountMinor, props.currency);

  const send = async (filedMonthReason?: string) => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (filedMonthReason) body[FILED_MONTH_REASON_FIELD] = filedMonthReason;
      const answer = await rawApi<{ cash: CashOutcome }>('POST', props.path, body);
      notifyDone(
        'Payment taken back',
        `${props.consequence} ${whatHappenedToTheRegister(answer.cash, amount)}`,
      );
      setFiledMonth(null);
      props.onDone();
      props.onClose();
    } catch (err) {
      // The month the payment sat in has already been filed. Not a refusal — the owner says why,
      // and the same request goes again carrying his words for his accountant to read.
      if (isFiledMonthWarning(err)) setFiledMonth(err);
      else notifyError(err, 'take this payment back');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        opened={filedMonth === null}
        onClose={props.onClose}
        title="Take this payment back"
        size="lg"
        // A half-typed reason lost to a stray click means the correction is lost with it.
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Alert color="yellow" variant="light" title={`${amount} — ${props.subject}`}>
            {props.consequence} Nothing is deleted: this payment stays on the list beside the row that undoes
            it, with who did it, when, and why.
          </Alert>
          <Textarea
            label="Why is it being taken back?"
            description="Whoever asks about this figure in six weeks reads this. A few words is enough."
            placeholder="Typed a zero too many — the gas man was handed L 100.00"
            autosize
            minRows={2}
            maxLength={300}
            value={reason}
            onChange={(e) => {
              setReason(e.currentTarget.value);
              setTouched(true);
            }}
            error={touched && tooShort ? 'Say why, so this can be explained later.' : null}
            disabled={saving}
          />
          <Text size="xs" c="dimmed">
            Where the cash came out of a register it goes back into that register, as a movement on the
            register screen. Where that drawer has already been counted off nothing moves — its over and short
            has already absorbed the difference.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={props.onClose} disabled={saving}>
              Leave it as it is
            </Button>
            <Button
              color="yellow"
              loading={saving}
              onClick={() => {
                setTouched(true);
                if (!tooShort) void send();
              }}
            >
              Take it back
            </Button>
          </Group>
        </Stack>
      </Modal>

      <WritingIntoAFiledMonth
        warning={filedMonth}
        saving={saving}
        onCancel={() => {
          setFiledMonth(null);
          props.onClose();
        }}
        onConfirm={(why) => void send(why)}
      />
    </>
  );
}
